// Invitation templates. Each one is pure data: InvitationCard turns the
// palette, fonts and ornament into the rendered design, so adding a template
// means adding an entry here (and, for a new ornament, a case in Ornament.jsx).
//
//   site       which website layout the invitation opens into (see
//              components/InvitationPage.jsx): "editorial", "cinematic",
//              "boutique", "playful", "conference", "poster" or "postcard"
//   backdrop   the animated background the site uses unless the host picks
//              another (see components/Backdrop.jsx)
//   photo      how the cover photo is worked into the design once the host
//              adds one: "circle", "frame", "arch", "banner" or "full"
//   kids       "boy" or "girl": a children's birthday theme; `scene` names
//              its illustration in components/KidsCovers.jsx
//   exclusive  a one-off design: `site` then names a cover in
//              components/ExclusiveCovers.jsx used by this template alone
//   dark       light text on a dark background
//   colorable  false when the host can't recolour the design
// Prices are in rand. prices.json holds each template's default price (the
// API reads the same file, see deploy/deploy.sh); the prices charged are set
// in the admin dashboard and read through context/CatalogContext.js.
import PRICES from "./prices.json";

export const CURRENCY = "ZAR";

const FONTS = {
  // Each stack ends in fonts every device has, so a design keeps its
  // character even if the web fonts fail to load.
  outfit: "'Outfit', 'Avenir Next', 'Segoe UI', sans-serif",
  fredoka: "'Fredoka', 'Arial Rounded MT Bold', 'Trebuchet MS', sans-serif",
  playfair: "'Playfair Display', 'Didot', 'Georgia', serif",
  cormorant: "'Cormorant Garamond', 'Garamond', 'Georgia', serif",
  script: "'Great Vibes', 'Snell Roundhand', 'Apple Chancery', 'Segoe Script', cursive",
  dmSerif: "'DM Serif Display', 'Didot', 'Georgia', serif",
  bebas: "'Bebas Neue', 'Impact', 'Arial Narrow', sans-serif",
  caveat: "'Caveat', 'Bradley Hand', 'Segoe Script', cursive",
  grotesk: "'Space Grotesk', 'Avenir Next', 'Segoe UI', sans-serif",
  abril: "'Abril Fatface', 'Didot', 'Georgia', serif",
  marcellus: "'Marcellus', 'Trajan Pro', 'Georgia', serif",
  mono: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
};

export const TEMPLATES = [
  {
    id: "jardin",
    site: "editorial",
    backdrop: "petals",
    photo: "circle",
    name: "Jardin",
    types: ["wedding", "babyshower", "dinner"],
    ornament: "floral",
    palette: { bg: "#f7f3ea", ink: "#3d4a3a", accent: "#7d9471", accent2: "#d9a5a0" },
    fonts: { display: FONTS.script, body: FONTS.cormorant },
    titleScale: 1.25,
  },
  {
    id: "rose-eternelle",
    site: "editorial",
    backdrop: "petals",
    photo: "circle",
    name: "Rose Éternelle",
    types: ["wedding"],
    ornament: "floral",
    palette: { bg: "#fff5f5", ink: "#5b2333", accent: "#c9566b", accent2: "#e8b4b8" },
    fonts: { display: FONTS.playfair, body: FONTS.cormorant },
  },
  {
    id: "minuit-dore",
    site: "cinematic",
    backdrop: "bokeh",
    photo: "frame",
    dark: true,
    name: "Minuit Doré",
    types: ["wedding", "graduation", "dinner"],
    ornament: "deco",
    palette: {
      bg: "linear-gradient(160deg, #0d1b3e 0%, #162a5c 100%)",
      ink: "#f3e9d2",
      accent: "#d4af6a",
      accent2: "#d4af6a",
    },
    fonts: { display: FONTS.playfair, body: FONTS.cormorant },
  },
  {
    id: "pure",
    site: "editorial",
    backdrop: "none",
    photo: "frame",
    name: "Pure",
    types: ["wedding", "meeting", "other"],
    ornament: "minimal",
    palette: { bg: "#ffffff", ink: "#1c1c1c", accent: "#1c1c1c", accent2: "#9a9a9a" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant },
    uppercaseTitle: true,
  },
  {
    id: "terracotta",
    site: "boutique",
    backdrop: "none",
    photo: "arch",
    name: "Terracotta",
    types: ["dinner", "wedding", "other"],
    ornament: "arch",
    palette: { bg: "#f6e7dc", ink: "#5a2d1c", accent: "#c2623f", accent2: "#e3b89f" },
    fonts: { display: FONTS.dmSerif, body: FONTS.outfit },
  },
  {
    id: "confetti-pop",
    site: "playful",
    backdrop: "bokeh",
    photo: "circle",
    name: "Pop",
    types: ["birthday", "graduation", "other"],
    ornament: "minimal",
    palette: { bg: "#fff7f0", ink: "#2a1f4d", accent: "#e8556d", accent2: "#ffc98a" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "ballons",
    site: "playful",
    backdrop: "bokeh",
    photo: "circle",
    name: "Ballons",
    types: ["birthday", "babyshower"],
    ornament: "balloons",
    palette: { bg: "#ffe9ef", ink: "#6b1e3f", accent: "#ff6f91", accent2: "#ffb86b" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "neon-night",
    site: "cinematic",
    backdrop: "stars",
    photo: "full",
    dark: true,
    name: "Neon Night",
    types: ["birthday", "other"],
    ornament: "stars",
    palette: {
      bg: "linear-gradient(165deg, #1a0b3b 0%, #3b0f6b 100%)",
      ink: "#ffffff",
      accent: "#ff5fd2",
      accent2: "#5ce1ff",
    },
    fonts: { display: FONTS.outfit, body: FONTS.outfit },
    uppercaseTitle: true,
  },
  {
    id: "petit-nuage",
    site: "playful",
    backdrop: "bokeh",
    photo: "circle",
    name: "Petit Nuage",
    types: ["babyshower", "birthday"],
    ornament: "clouds",
    palette: { bg: "#eaf4ff", ink: "#2b4a6f", accent: "#7fb2e5", accent2: "#ffd98a" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "boardroom",
    site: "conference",
    backdrop: "none",
    photo: "banner",
    name: "Boardroom",
    types: ["meeting"],
    ornament: "geo",
    palette: { bg: "#f4f6fb", ink: "#0d1b5e", accent: "#2563eb", accent2: "#ff9500" },
    fonts: { display: FONTS.outfit, body: FONTS.outfit },
  },
  {
    id: "slate",
    site: "cinematic",
    backdrop: "aurora",
    photo: "full",
    dark: true,
    name: "Slate",
    types: ["meeting", "other"],
    ornament: "minimal",
    palette: { bg: "#1f2530", ink: "#f2f4f7", accent: "#8fb4ff", accent2: "#8fb4ff" },
    fonts: { display: FONTS.outfit, body: FONTS.outfit },
  },
  {
    id: "summit",
    site: "conference",
    backdrop: "aurora",
    photo: "banner",
    dark: true,
    colorable: false,
    name: "Summit",
    types: ["meeting", "graduation"],
    ornament: "geo",
    palette: {
      bg: "linear-gradient(150deg, #ff9500 0%, #e91e7a 100%)",
      ink: "#ffffff",
      accent: "rgba(255, 255, 255, 0.28)",
      accent2: "rgba(13, 27, 94, 0.35)",
    },
    fonts: { display: FONTS.outfit, body: FONTS.outfit },
  },
  {
    id: "laureat",
    site: "cinematic",
    backdrop: "bokeh",
    photo: "frame",
    dark: true,
    name: "Lauréat",
    types: ["graduation"],
    ornament: "deco",
    palette: { bg: "#0f3d2e", ink: "#f4efdc", accent: "#e0b95b", accent2: "#e0b95b" },
    fonts: { display: FONTS.playfair, body: FONTS.cormorant },
  },
  // ---- weddings and dinners
  {
    id: "olivier",
    site: "editorial",
    backdrop: "none",
    photo: "arch",
    name: "Olivier",
    types: ["wedding", "dinner"],
    ornament: "laurel",
    palette: { bg: "#f4f1e6", ink: "#2f3a2a", accent: "#6b7f4f", accent2: "#c9b98a" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant },
  },
  {
    id: "lettre-damour",
    site: "postcard",
    backdrop: "petals",
    photo: "frame",
    name: "Lettre d'Amour",
    types: ["wedding", "dinner"],
    ornament: "hearts",
    palette: { bg: "#fbf3ee", ink: "#6a1f2b", accent: "#b8323f", accent2: "#f0c4c0" },
    fonts: { display: FONTS.caveat, body: FONTS.cormorant },
    titleScale: 1.3,
  },
  {
    id: "riviera",
    site: "boutique",
    backdrop: "none",
    photo: "arch",
    name: "Riviera",
    types: ["wedding", "dinner", "other"],
    ornament: "waves",
    palette: { bg: "#eef5f7", ink: "#123a52", accent: "#1f7a9c", accent2: "#f2c078" },
    fonts: { display: FONTS.playfair, body: FONTS.outfit },
  },
  {
    id: "opera",
    site: "poster",
    backdrop: "none",
    photo: "frame",
    dark: true,
    name: "Opéra",
    types: ["wedding", "other"],
    ornament: "deco",
    palette: { bg: "#111111", ink: "#f5efe0", accent: "#c9a24b", accent2: "#c9a24b" },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  {
    id: "champagne",
    site: "cinematic",
    backdrop: "bokeh",
    photo: "full",
    dark: true,
    name: "Champagne",
    types: ["wedding", "dinner", "graduation"],
    ornament: "sunburst",
    palette: {
      bg: "linear-gradient(160deg, #2b1d14 0%, #5a3d26 100%)",
      ink: "#fbeed8",
      accent: "#e3c08a",
      accent2: "#e3c08a",
    },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant },
  },
  // ---- birthdays and parties
  {
    id: "fiesta",
    site: "poster",
    backdrop: "confetti",
    photo: "banner",
    name: "Fiesta",
    types: ["birthday", "graduation", "other"],
    ornament: "bunting",
    palette: { bg: "#fff4d6", ink: "#1f1147", accent: "#ff5a3c", accent2: "#00b8a9" },
    fonts: { display: FONTS.bebas, body: FONTS.outfit },
    titleScale: 1.25,
  },
  {
    id: "sorbet",
    site: "playful",
    backdrop: "bokeh",
    photo: "circle",
    name: "Sorbet",
    types: ["birthday", "babyshower"],
    ornament: "minimal",
    palette: { bg: "#fff0f6", ink: "#4a2a5a", accent: "#ff8fab", accent2: "#b9e6d3" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "arcade",
    site: "poster",
    backdrop: "stars",
    photo: "full",
    dark: true,
    name: "Arcade",
    types: ["birthday", "other"],
    ornament: "grid",
    palette: { bg: "#0f0f1a", ink: "#f5f5ff", accent: "#00f0ff", accent2: "#ff3df0" },
    fonts: { display: FONTS.grotesk, body: FONTS.grotesk },
    uppercaseTitle: true,
  },
  {
    id: "gateau",
    site: "postcard",
    backdrop: "confetti",
    photo: "circle",
    name: "Gâteau",
    types: ["birthday", "babyshower", "dinner"],
    ornament: "stripes",
    palette: { bg: "#fef6e4", ink: "#3d2c2e", accent: "#f582ae", accent2: "#8bd3dd" },
    fonts: { display: FONTS.caveat, body: FONTS.outfit },
    titleScale: 1.3,
  },
  {
    id: "disco",
    site: "cinematic",
    backdrop: "bokeh",
    photo: "full",
    dark: true,
    name: "Disco",
    types: ["birthday", "graduation", "other"],
    ornament: "sunburst",
    palette: {
      bg: "linear-gradient(150deg, #2a0845 0%, #6441a5 100%)",
      ink: "#ffffff",
      accent: "#ffd166",
      accent2: "#ef476f",
    },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  {
    id: "jungle",
    site: "playful",
    backdrop: "petals",
    photo: "circle",
    name: "Jungle",
    types: ["birthday", "babyshower", "other"],
    ornament: "laurel",
    palette: { bg: "#eef7e9", ink: "#1f3d2b", accent: "#2f9e62", accent2: "#ffbe3d" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  // ---- meetings and conferences
  {
    id: "blueprint",
    site: "conference",
    backdrop: "none",
    photo: "banner",
    dark: true,
    name: "Blueprint",
    types: ["meeting", "graduation"],
    ornament: "grid",
    palette: { bg: "#0b2545", ink: "#eaf2ff", accent: "#7fb7ff", accent2: "#ffd166" },
    fonts: { display: FONTS.grotesk, body: FONTS.grotesk },
  },
  {
    id: "atelier",
    site: "editorial",
    backdrop: "none",
    photo: "frame",
    name: "Atelier",
    types: ["meeting", "dinner", "other"],
    ornament: "minimal",
    palette: { bg: "#f3efe8", ink: "#26221d", accent: "#b5532a", accent2: "#d9c7b0" },
    fonts: { display: FONTS.marcellus, body: FONTS.outfit },
  },
  {
    id: "keynote",
    site: "poster",
    backdrop: "none",
    photo: "banner",
    name: "Keynote",
    types: ["meeting", "graduation", "other"],
    ornament: "geo",
    palette: { bg: "#ffffff", ink: "#111111", accent: "#ff3b30", accent2: "#111111" },
    fonts: { display: FONTS.bebas, body: FONTS.grotesk },
    titleScale: 1.25,
  },
  {
    id: "horizon",
    site: "cinematic",
    backdrop: "aurora",
    photo: "full",
    dark: true,
    name: "Horizon",
    types: ["meeting", "other"],
    ornament: "waves",
    palette: {
      bg: "linear-gradient(170deg, #0f2027 0%, #2c5364 100%)",
      ink: "#f1f7fa",
      accent: "#7fdbda",
      accent2: "#7fdbda",
    },
    fonts: { display: FONTS.outfit, body: FONTS.outfit },
  },
  {
    id: "forum",
    site: "conference",
    backdrop: "none",
    photo: "banner",
    name: "Forum",
    types: ["meeting", "graduation"],
    ornament: "stripes",
    palette: { bg: "#f6f7f2", ink: "#1d3b2f", accent: "#2e8b57", accent2: "#f2b134" },
    fonts: { display: FONTS.grotesk, body: FONTS.grotesk },
  },
  {
    id: "memo",
    site: "postcard",
    backdrop: "none",
    photo: "frame",
    name: "Mémo",
    types: ["meeting", "dinner", "other"],
    ornament: "minimal",
    palette: { bg: "#faf9f6", ink: "#2b2b2b", accent: "#3d5a80", accent2: "#e0a458" },
    fonts: { display: FONTS.dmSerif, body: FONTS.outfit },
  },
  // ---- baby showers
  {
    id: "doudou",
    site: "playful",
    backdrop: "bokeh",
    photo: "circle",
    name: "Doudou",
    types: ["babyshower"],
    ornament: "hearts",
    palette: { bg: "#fdf3e7", ink: "#5a4636", accent: "#e6a57e", accent2: "#a8d5ba" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "cigogne",
    site: "postcard",
    backdrop: "none",
    photo: "circle",
    name: "Cigogne",
    types: ["babyshower"],
    ornament: "clouds",
    palette: { bg: "#eef3fb", ink: "#34496b", accent: "#8fa9d9", accent2: "#f6c7c7" },
    fonts: { display: FONTS.caveat, body: FONTS.outfit },
    titleScale: 1.3,
  },
  {
    id: "etoile-filante",
    site: "cinematic",
    backdrop: "stars",
    photo: "full",
    dark: true,
    name: "Étoile Filante",
    types: ["babyshower", "other"],
    ornament: "stars",
    palette: {
      bg: "linear-gradient(165deg, #1b2a4e 0%, #3c4f8a 100%)",
      ink: "#fdf6e3",
      accent: "#ffd98a",
      accent2: "#a9c5ff",
    },
    fonts: { display: FONTS.marcellus, body: FONTS.outfit },
  },
  {
    id: "berceuse",
    site: "editorial",
    backdrop: "petals",
    photo: "circle",
    name: "Berceuse",
    types: ["babyshower"],
    ornament: "minimal",
    palette: { bg: "#f7f2fb", ink: "#4b3b6b", accent: "#a78bda", accent2: "#f5c6d6" },
    fonts: { display: FONTS.playfair, body: FONTS.outfit },
  },
  // ---- children's birthdays: illustrated themes (components/KidsCovers.jsx)
  {
    id: "super-heros",
    kids: "boy",
    site: "kids",
    scene: "hero",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Super Héros",
    types: ["birthday"],
    ornament: "sunburst",
    palette: { bg: "#fff6cc", ink: "#14213d", accent: "#d62839", accent2: "#1d6fd8" },
    fonts: { display: FONTS.bebas, body: FONTS.outfit },
  },
  {
    id: "gardien-de-la-nuit",
    kids: "boy",
    site: "kids",
    scene: "night",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    dark: true,
    name: "Gardien de la Nuit",
    types: ["birthday"],
    ornament: "stars",
    palette: { bg: "#0f1630", ink: "#f4f1de", accent: "#ffd23f", accent2: "#8ea2ff" },
    fonts: { display: FONTS.bebas, body: FONTS.outfit },
  },
  {
    id: "roi-de-la-savane",
    kids: "boy",
    site: "kids",
    scene: "savanna",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Roi de la Savane",
    types: ["birthday"],
    ornament: "laurel",
    palette: { bg: "#fff1dc", ink: "#4a2c12", accent: "#b8560f", accent2: "#f6bd60" },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  {
    id: "aloha",
    kids: "girl",
    site: "kids",
    scene: "aloha",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Aloha",
    types: ["birthday"],
    ornament: "waves",
    palette: { bg: "#e8f8f7", ink: "#0b4f6c", accent: "#0a7f86", accent2: "#ff7aa2" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "petit-ange",
    kids: "girl",
    site: "kids",
    scene: "angel",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Petit Ange",
    types: ["birthday"],
    ornament: "clouds",
    palette: { bg: "#f6f3ff", ink: "#4a4470", accent: "#7a69c9", accent2: "#f5c542" },
    fonts: { display: FONTS.script, body: FONTS.outfit },
  },
  {
    id: "princesse-rose",
    kids: "girl",
    site: "kids",
    scene: "princess",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Princesse Rose",
    types: ["birthday"],
    ornament: "hearts",
    palette: { bg: "#fff0f6", ink: "#7a1f4b", accent: "#c2185b", accent2: "#f9b4d0" },
    fonts: { display: FONTS.playfair, body: FONTS.outfit },
  },
  {
    id: "sirene",
    kids: "girl",
    site: "kids",
    scene: "mermaid",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Sirène",
    types: ["birthday"],
    ornament: "waves",
    palette: { bg: "#e9fbf9", ink: "#23486b", accent: "#12727c", accent2: "#c69df2" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "glamour",
    kids: "girl",
    site: "kids",
    scene: "glam",
    backdrop: "none",
    photo: "circle",
    colorable: false,
    name: "Glamour",
    types: ["birthday"],
    ornament: "stripes",
    palette: { bg: "#ffe3f1", ink: "#5a0b36", accent: "#c80f6c", accent2: "#ffb000" },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  // ---- exclusive: one-off designs, three per gathering type
  {
    id: "sceau",
    exclusive: true,
    site: "crest",
    backdrop: "none",
    photo: "circle",
    name: "Sceau",
    types: ["wedding"],
    ornament: "laurel",
    palette: { bg: "#f6f1e7", ink: "#2c2a26", accent: "#9c2f2f", accent2: "#c9b38a" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant },
  },
  {
    id: "pellicule",
    exclusive: true,
    site: "film",
    backdrop: "none",
    photo: "full",
    dark: true,
    name: "Pellicule",
    types: ["wedding"],
    ornament: "minimal",
    palette: { bg: "#121212", ink: "#f2ede3", accent: "#e0b84c", accent2: "#e0b84c" },
    fonts: { display: FONTS.playfair, body: FONTS.outfit },
  },
  {
    id: "gazette",
    exclusive: true,
    site: "gazette",
    backdrop: "none",
    photo: "frame",
    name: "La Gazette",
    types: ["wedding"],
    ornament: "minimal",
    palette: { bg: "#f4f0e6", ink: "#1a1a1a", accent: "#1a1a1a", accent2: "#8a8478" },
    fonts: { display: FONTS.abril, body: FONTS.cormorant },
  },
  {
    id: "billet",
    exclusive: true,
    site: "ticket",
    backdrop: "none",
    photo: "banner",
    name: "Billet",
    types: ["birthday"],
    ornament: "stripes",
    palette: { bg: "#fdf3e3", ink: "#1d2440", accent: "#f47a55", accent2: "#f2b134" },
    fonts: { display: FONTS.bebas, body: FONTS.outfit },
  },
  {
    id: "enseigne",
    exclusive: true,
    site: "neon",
    backdrop: "none",
    photo: "full",
    dark: true,
    name: "Enseigne",
    types: ["birthday"],
    ornament: "minimal",
    palette: { bg: "#1a1016", ink: "#fff0f8", accent: "#ff4fd8", accent2: "#4fe3ff" },
    fonts: { display: FONTS.script, body: FONTS.outfit },
  },
  {
    id: "vinyle",
    exclusive: true,
    site: "vinyl",
    backdrop: "none",
    photo: "circle",
    name: "Vinyle",
    types: ["birthday"],
    ornament: "sunburst",
    palette: { bg: "#f3e9dc", ink: "#23201d", accent: "#d1462f", accent2: "#2f6f6a" },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  {
    id: "terminal",
    exclusive: true,
    site: "terminal",
    backdrop: "none",
    photo: "full",
    dark: true,
    name: "Terminal",
    types: ["meeting"],
    ornament: "grid",
    palette: { bg: "#0d1117", ink: "#d6e2f0", accent: "#3fb950", accent2: "#58a6ff" },
    fonts: { display: FONTS.mono, body: FONTS.mono },
  },
  {
    id: "embarquement",
    exclusive: true,
    site: "boarding",
    backdrop: "none",
    photo: "banner",
    name: "Embarquement",
    types: ["meeting"],
    ornament: "stripes",
    palette: { bg: "#e9eef5", ink: "#14213d", accent: "#1d4ed8", accent2: "#b45309" },
    fonts: { display: FONTS.grotesk, body: FONTS.grotesk },
  },
  {
    id: "ephemeride",
    exclusive: true,
    site: "calendar",
    backdrop: "none",
    photo: "frame",
    name: "Éphéméride",
    types: ["meeting"],
    ornament: "minimal",
    palette: { bg: "#fafafa", ink: "#18181b", accent: "#dc2626", accent2: "#a1a1aa" },
    fonts: { display: FONTS.grotesk, body: FONTS.outfit },
  },
  {
    id: "mobile",
    exclusive: true,
    site: "mobile",
    backdrop: "none",
    photo: "circle",
    name: "Mobile",
    types: ["babyshower"],
    ornament: "clouds",
    palette: { bg: "#eef4fb", ink: "#33415c", accent: "#7d9bd1", accent2: "#f4c9a8" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "cubes",
    exclusive: true,
    site: "blocks",
    backdrop: "none",
    photo: "circle",
    name: "Cubes",
    types: ["babyshower"],
    ornament: "minimal",
    palette: { bg: "#fff8ec", ink: "#3a3350", accent: "#e2714f", accent2: "#4fae9a" },
    fonts: { display: FONTS.fredoka, body: FONTS.outfit },
  },
  {
    id: "conte",
    exclusive: true,
    site: "storybook",
    backdrop: "none",
    photo: "arch",
    name: "Conte",
    types: ["babyshower"],
    ornament: "floral",
    palette: { bg: "#f3ead8", ink: "#3b2f2a", accent: "#8c5a3c", accent2: "#b9c9a3" },
    fonts: { display: FONTS.dmSerif, body: FONTS.cormorant },
  },
  {
    id: "parchemin",
    exclusive: true,
    site: "diploma",
    backdrop: "none",
    photo: "frame",
    name: "Parchemin",
    types: ["graduation"],
    ornament: "deco",
    palette: { bg: "#fbf7ec", ink: "#1f2a44", accent: "#b08d3c", accent2: "#1f2a44" },
    fonts: { display: FONTS.script, body: FONTS.cormorant },
  },
  {
    id: "varsity",
    exclusive: true,
    site: "varsity",
    backdrop: "none",
    photo: "banner",
    dark: true,
    name: "Varsity",
    types: ["graduation"],
    ornament: "stripes",
    palette: { bg: "#14213d", ink: "#fdf6e3", accent: "#fca311", accent2: "#e5e5e5" },
    fonts: { display: FONTS.bebas, body: FONTS.outfit },
  },
  {
    id: "ardoise",
    exclusive: true,
    site: "chalk",
    backdrop: "none",
    photo: "frame",
    dark: true,
    name: "Ardoise",
    types: ["graduation"],
    ornament: "minimal",
    palette: { bg: "#23332d", ink: "#f3f1e7", accent: "#f7d774", accent2: "#f2a0a0" },
    fonts: { display: FONTS.caveat, body: FONTS.caveat },
    titleScale: 1.3,
  },
  {
    id: "la-carte",
    exclusive: true,
    site: "menu",
    backdrop: "none",
    photo: "frame",
    name: "La Carte",
    types: ["dinner"],
    ornament: "minimal",
    palette: { bg: "#f7f4ee", ink: "#2a2622", accent: "#7a5c3e", accent2: "#c8b8a2" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant },
  },
  {
    id: "nappe",
    exclusive: true,
    site: "plate",
    backdrop: "none",
    photo: "circle",
    name: "Nappe",
    types: ["dinner"],
    ornament: "minimal",
    palette: { bg: "#fbfaf7", ink: "#26323a", accent: "#c0392b", accent2: "#e8b4ae" },
    fonts: { display: FONTS.dmSerif, body: FONTS.outfit },
  },
  {
    id: "cuvee",
    exclusive: true,
    site: "label",
    backdrop: "none",
    photo: "frame",
    dark: true,
    name: "Cuvée",
    types: ["dinner"],
    ornament: "deco",
    palette: { bg: "#2a0f17", ink: "#f6ead2", accent: "#c9a24b", accent2: "#c9a24b" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant },
  },
  {
    id: "passeport",
    exclusive: true,
    site: "passport",
    backdrop: "none",
    photo: "frame",
    name: "Passeport",
    types: ["other"],
    ornament: "grid",
    palette: { bg: "#eef0ea", ink: "#1b2a3a", accent: "#1f6f5c", accent2: "#b23a48" },
    fonts: { display: FONTS.grotesk, body: FONTS.outfit },
  },
  {
    id: "cliches",
    exclusive: true,
    site: "stack",
    backdrop: "none",
    photo: "frame",
    name: "Clichés",
    types: ["other"],
    ornament: "minimal",
    palette: { bg: "#f4efe9", ink: "#201c1a", accent: "#c9502f", accent2: "#3f7cac" },
    fonts: { display: FONTS.abril, body: FONTS.outfit },
  },
  {
    id: "astral",
    exclusive: true,
    site: "starmap",
    backdrop: "none",
    photo: "full",
    dark: true,
    name: "Astral",
    types: ["other"],
    ornament: "stars",
    palette: { bg: "#0b1026", ink: "#eef2ff", accent: "#9fb4ff", accent2: "#ffd98a" },
    fonts: { display: FONTS.marcellus, body: FONTS.outfit },
  },

  // ---- Keepsake: phone-first stationery invitations (components/Keepsake.jsx).
  // `hero` picks the cover (envelope, arch, polaroid, wreath); `flora` colours
  // the botanical artwork and `flora.ground` is the deep colour around the page.
  {
    id: "seal-burgundy",
    site: "keepsake",
    hero: "envelope",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Velvet Seal · Burgundy",
    types: ["wedding", "dinner", "other"],
    ornament: "floral",
    palette: { bg: "#f6efe6", ink: "#4a1520", accent: "#5c1424", accent2: "#c9a45c" },
    flora: { style: "lush", ground: "#4a0f1b", bloom: "#8e1f33", deep: "#4f0f1c", light: "#efe2d4", leaf: "#8a9a6b", metal: "#c9a45c" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "seal-navy",
    site: "keepsake",
    hero: "envelope",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Velvet Seal · Navy",
    types: ["wedding", "dinner", "other"],
    ornament: "floral",
    palette: { bg: "#f3f1ec", ink: "#1c2a4a", accent: "#1f2f55", accent2: "#c9a45c" },
    flora: { style: "lush", ground: "#141f3a", bloom: "#4a6a9e", deep: "#1c2b4f", light: "#eef1f7", leaf: "#7f9a86", metal: "#c9a45c" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "seal-sage",
    site: "keepsake",
    hero: "envelope",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Velvet Seal · Sage",
    types: ["wedding", "dinner", "other"],
    ornament: "floral",
    palette: { bg: "#f4f3ec", ink: "#3f4a3a", accent: "#62735a", accent2: "#b8954a" },
    flora: { style: "lush", ground: "#4d5a45", bloom: "#f4efe4", deep: "#9c8f72", light: "#ffffff", leaf: "#7d8f6a", metal: "#b8954a" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "seal-mocha",
    site: "keepsake",
    hero: "envelope",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Velvet Seal · Mocha",
    types: ["wedding", "dinner", "other"],
    ornament: "floral",
    palette: { bg: "#f5eee6", ink: "#4a3426", accent: "#6b4a36", accent2: "#c9a45c" },
    flora: { style: "lush", ground: "#3d2a1f", bloom: "#c58b6a", deep: "#7a4a30", light: "#efd9c3", leaf: "#a39a72", metal: "#c9a45c" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "arch-blush",
    site: "keepsake",
    hero: "arch",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Garden Arch · Blush",
    types: ["wedding", "babyshower", "birthday", "dinner"],
    ornament: "floral",
    palette: { bg: "#fbf3f1", ink: "#6b3a46", accent: "#b8687b", accent2: "#d9b48a" },
    flora: { style: "lush", ground: "#a95c70", bloom: "#f0b8c4", deep: "#b86a7e", light: "#fff4f6", leaf: "#9bae8c", metal: "#d9b48a" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "arch-sage",
    site: "keepsake",
    hero: "arch",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Garden Arch · Sage",
    types: ["wedding", "babyshower", "birthday", "dinner"],
    ornament: "floral",
    palette: { bg: "#f2f5ee", ink: "#344232", accent: "#5f7a58", accent2: "#c2a36b" },
    flora: { style: "lush", ground: "#3e5239", bloom: "#fbfaf5", deep: "#a9b39a", light: "#ffffff", leaf: "#6f8f63", metal: "#c2a36b" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "arch-lilac",
    site: "keepsake",
    hero: "arch",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Garden Arch · Lilac",
    types: ["wedding", "babyshower", "birthday", "dinner"],
    ornament: "floral",
    palette: { bg: "#f6f2fa", ink: "#3f2f5a", accent: "#7a62a8", accent2: "#c8b07a" },
    flora: { style: "lush", ground: "#3c2d5c", bloom: "#c9b6e6", deep: "#8b6fbd", light: "#f3edfb", leaf: "#8aa38c", metal: "#c8b07a" },
    fonts: { display: FONTS.cormorant, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "memoir-cream",
    site: "keepsake",
    hero: "polaroid",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Memoir · Cream",
    types: ["wedding", "birthday", "graduation", "other"],
    ornament: "floral",
    palette: { bg: "#f6f1e7", ink: "#3b3229", accent: "#8a6f52", accent2: "#b89255" },
    flora: { style: "dried", ground: "#2f2821", bloom: "#d9a066", deep: "#9c6436", light: "#f3e6cf", leaf: "#9c9a6a", metal: "#b89255" },
    fonts: { display: FONTS.playfair, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "memoir-dusk",
    site: "keepsake",
    hero: "polaroid",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Memoir · Dusk",
    types: ["wedding", "birthday", "graduation", "other"],
    ornament: "floral",
    palette: { bg: "#eef0f3", ink: "#2c3340", accent: "#55657e", accent2: "#b8a07a" },
    flora: { style: "dried", ground: "#252c38", bloom: "#e2b3a3", deep: "#a86e60", light: "#f6ece8", leaf: "#8b9c94", metal: "#b8a07a" },
    fonts: { display: FONTS.playfair, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "laurel-ivory",
    site: "keepsake",
    hero: "wreath",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Laurel · Ivory",
    types: ["wedding", "dinner", "graduation", "other"],
    ornament: "floral",
    palette: { bg: "#fbf8f1", ink: "#2f3a2e", accent: "#4f6646", accent2: "#b99a5b" },
    flora: { style: "lush", ground: "#2b3a28", bloom: "#fdfbf5", deep: "#c3b896", light: "#ffffff", leaf: "#5f7d55", metal: "#b99a5b" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "laurel-gold",
    site: "keepsake",
    hero: "wreath",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Laurel · Gold",
    types: ["wedding", "dinner", "graduation", "other"],
    ornament: "floral",
    palette: { bg: "#fbf6ea", ink: "#3a2f18", accent: "#8a6c2a", accent2: "#c9a24c" },
    flora: { style: "lush", ground: "#2c2414", bloom: "#f3e2b4", deep: "#b8913f", light: "#fff8e6", leaf: "#8f9a6a", metal: "#c9a24c" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant, script: FONTS.script },
  },
  {
    id: "laurel-dusty-blue",
    site: "keepsake",
    hero: "wreath",
    backdrop: "petals",
    photo: "arch",
    isNew: true,
    colorable: false,
    name: "Laurel · Dusty Blue",
    types: ["wedding", "dinner", "graduation", "other"],
    ornament: "floral",
    palette: { bg: "#f3f6f8", ink: "#26384a", accent: "#4f6f8c", accent2: "#b9a37a" },
    flora: { style: "lush", ground: "#23364a", bloom: "#c7d8e6", deep: "#7d9fbd", light: "#f5f9fc", leaf: "#7f9a8a", metal: "#b9a37a" },
    fonts: { display: FONTS.marcellus, body: FONTS.cormorant, script: FONTS.script },
  },
];

TEMPLATES.forEach((template) => {
  template.price = PRICES[template.id];
});

// Colour palettes a host can apply on top of any colourable template.
// "default" keeps the template's own colours.
export const COLORWAYS = [
  { id: "default" },
  { id: "rose", bg: "#fff1f4", ink: "#5b2333", accent: "#d9567c", accent2: "#f3b6c6" },
  { id: "sky", bg: "#eef6ff", ink: "#1f3f66", accent: "#4a90d9", accent2: "#a9d0f5" },
  { id: "sage", bg: "#f3f6ee", ink: "#34432f", accent: "#7d9471", accent2: "#d9b08c" },
  { id: "gold", bg: "#fbf6ea", ink: "#4a3a17", accent: "#c49a3a", accent2: "#e6cf9a" },
  { id: "lilac", bg: "#f6f1fd", ink: "#3c2a66", accent: "#8e63d9", accent2: "#e3b5ee" },
  { id: "sunset", bg: "#fff3ea", ink: "#5a2616", accent: "#f0683c", accent2: "#ffc078" },
];

// Picking "girl" or "boy" selects the matching palette.
export const GENDER_COLORWAYS = { girl: "rose", boy: "sky", neutral: "default" };

// The template as it should render for this event: the host's palette is
// applied on top of the template's own. Dark templates keep their background
// and only take the palette's (lighter) accent so text stays readable.
export const resolveTemplate = (template, event) => {
  const colorway = COLORWAYS.find((candidate) => candidate.id === event?.look?.colorway);
  if (!colorway || colorway.id === "default" || template.colorable === false) return template;
  const palette = template.dark
    ? { ...template.palette, accent: colorway.accent2, accent2: colorway.accent2 }
    : { bg: colorway.bg, ink: colorway.ink, accent: colorway.accent, accent2: colorway.accent2 };
  return { ...template, palette };
};

// The two colours shown on a palette's picker button.
export const swatchStyle = (template, colorwayId) => {
  const { accent, accent2 } = resolveTemplate(template, { look: { colorway: colorwayId } }).palette;
  return { "--swatch": accent, "--swatch2": accent2 };
};

export const getTemplate = (id) => TEMPLATES.find((template) => template.id === id) || TEMPLATES[0];

// Exclusive designs are listed first. Pages use useCatalog().templatesForType,
// which also follows the admin dashboard (hidden and featured templates).
export const templatesForType = (typeId) =>
  (typeId ? TEMPLATES.filter((template) => template.types.includes(typeId)) : [...TEMPLATES]).sort(
    (a, b) => Number(Boolean(b.exclusive)) - Number(Boolean(a.exclusive))
  );

export const formatPrice = (amount) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: CURRENCY,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

// Animated hero backgrounds a host can choose from. "auto" follows the
// template; "none" switches motion off.
export const BACKDROPS = ["auto", "none", "petals", "bokeh", "confetti", "stars", "aurora"];

export const backdropFor = (template, event) => {
  const choice = event?.backdrop || "auto";
  return choice === "auto" ? template.backdrop || "none" : choice;
};

// Ready-made cover videos. Files live in public/videos; to offer more clips,
// drop licensed .mp4 files there and list them here.
export const STOCK_VIDEOS = [
  {
    id: "bloom",
    src: `${process.env.PUBLIC_URL || ""}/videos/bloom.mp4`,
    // Still frame shown while the clip loads, and in thumbnails.
    poster: `${process.env.PUBLIC_URL || ""}/videos/bloom.jpg`,
    // "flower.mp4" from MDN's shared assets, dedicated to the public domain (CC0).
    credit: "MDN / CC0",
  },
];

export const stockVideo = (id) => STOCK_VIDEOS.find((video) => video.id === id) || null;
